import { omit, noop } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { getTextContentWithDelimiter } from '~/common/domUtils'
import { useMsg } from '~/common/localizationUtils'
import { useLatestCallbackRef } from '~/hooks/useLatestRef'
import { TOP_NAV_HEIGHT_PX } from '~/common/scrollUtils'
import { $thm } from '~/common/themeSx'
import { insetDivider } from '../../../foundations/data_display/Separators'
import {
  Box,
  BoxProps,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  Flex,
  FlexProps,
  Heading,
  HeadingProps,
  forwardRef
} from '../../../chakra'
import { useSplitListContext } from './context'

type SplitListDetailProps = FlexProps & {
  /**
   * If you need to force the drawer to be open you can set this to true. All other values will allow the
   * drawer to be managed by the internal state.
   */
  forceDrawerOpen?: boolean
  onCloseDrawer?: () => void
}

/**
 * SplitList.Detail
 *
 * The content for the Split List comprises on many child components. This component is a child component of SplitList,
 * and used as a wrapper component to represent the content of the Split List.
 *
 * This component accepts BoxProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const Detail = (props: SplitListDetailProps) => {
  const isMobile = useSplitListContext.select(s => s?.isMobile)
  return isMobile ? (
    <SplitListMobileContainer {...props} />
  ) : (
    <SplitListDesktopContainer {...omit(props, 'forceDrawerOpen', 'onCloseDrawer')} />
  )
}

Detail.displayName = 'SplitList.Detail'

// A hook to detect a "Swipe Right" on the drawer to close it via swipe gesture
function useSwipeRight(callback: () => void) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [captured, setCaptured] = useState(0)
  const refCb = (el: HTMLDivElement | null) => {
    if (el && ref.current !== el) {
      ref.current = el
      setCaptured(prev => prev + 1)
    }
  }
  const latestCallback = useLatestCallbackRef(callback)

  useEffect(() => {
    const element = ref.current
    if (!element) return undefined

    let touchStartX = 0
    let touchStartY = 0
    let touchEndX = 0
    let touchEndY = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX
      touchStartY = e.changedTouches[0].screenY
    }

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX
      touchEndY = e.changedTouches[0].screenY
      handleGesture()
    }

    const handleGesture = () => {
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      // Check for horizontal swipe with a threshold and ensure it's more horizontal than vertical
      if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > 0) {
          // Swipe right
          latestCallback()
        }
      }
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [captured])

  return refCb
}

function useSmartContainerAriaLabel() {
  const msg = useMsg()
  const [ariaLabel, setAriaLabel] = useState(msg('common.ui.item_details'))
  return {
    'aria-label': ariaLabel,
    ref: (el: HTMLElement | null) => {
      if (!el) return
      // We look for a heading inside SplitList.DetailHeader to use as the aria-label
      // We find the first element that matches either [data-split-list-heading] or any h1-h4
      // If there is no split-list-header we look for those the first match of those same elements anywhere within the container
      const heading = el.querySelector(
        '#split-list-header :is([data-split-list-heading], h1, h2, h3, h4), [data-split-list-heading], h1, h2, h3, h4'
      )
      setAriaLabel(
        heading
          ? msg('common.ui.details_for_item', { item: getTextContentWithDelimiter(heading) })
          : msg('common.ui.item_details')
      )
    }
  }
}

function SplitListMobileContainer({ forceDrawerOpen, onCloseDrawer, sx, ...props }: SplitListDetailProps) {
  const drawerIsOpenCtx = useSplitListContext.select(s => s?.drawerIsOpen) ?? false
  const drawerIsOpen = forceDrawerOpen === true || drawerIsOpenCtx
  const setDrawerIsOpen = useSplitListContext.select(s => s?.setDrawerIsOpen) ?? noop
  const onClose = () => {
    onCloseDrawer?.()
    setDrawerIsOpen?.(false)
  }
  const swipeRef = useSwipeRight(() => {
    setDrawerIsOpen?.(false)
  })
  const containerSmartAriaLabelProps = useSmartContainerAriaLabel()

  return (
    <Drawer size="full" isOpen={!!drawerIsOpen} placement="right" onClose={onClose}>
      <DrawerContent
        sx={{
          mt: `calc(${TOP_NAV_HEIGHT_PX})`,
          h: `calc(100vh - ${TOP_NAV_HEIGHT_PX} - 1px)`,
          borderTop: '1px',
          borderColor: 'system.color.border.container'
        }}>
        <DrawerBody p={0} ref={swipeRef}>
          <Button
            variant="link"
            textAlign="left"
            textDecoration="underline"
            borderRadius={0}
            alignSelf="flex-start"
            p={6}
            mt={2}
            onClick={() => {
              onClose()
            }}>
            Back to List
          </Button>
          <Box
            id="split-list-content-container"
            data-split-list-content-container="mobile"
            marginInlineStart="0"
            borderLeftWidth="1px"
            borderLeftStyle="solid"
            borderLeftColor="var(--vndly-color-container-border)"
            overflow="auto"
            bg="white"
            w="full"
            display="flex"
            flex={1}
            flexDir="column"
            role="main"
            tabIndex={-1}
            {...containerSmartAriaLabelProps}
            sx={$thm({
              borderLeft: { $base: '1px solid var(--vndly-color-container-border)', $canvas: 'none' },
              ...sx
            })}
            {...props}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

function SplitListDesktopContainer({ sx, ...props }: Omit<SplitListDetailProps, 'forceDrawerOpen' | 'onCloseDrawer'>) {
  const containerSmartAriaLabelProps = useSmartContainerAriaLabel()
  return (
    <Box
      id="split-list-content-container"
      data-split-list-content-container="desktop"
      marginInlineStart="1px !important"
      borderLeftColor="var(--vndly-color-container-border)"
      overflow="auto"
      bg="white"
      w="full"
      display="flex"
      flex={1}
      flexDir="column"
      role="main"
      tabIndex={-1}
      sx={$thm({
        pl: { $base: 0, $canvas: 6 },
        pr: { $base: 0, $canvas: 3 },
        py: { $base: 0, $canvas: 3 },
        borderLeft: { $base: '1px solid var(--vndly-color-container-border)', $canvas: 'none' },
        // Pane divider lives on the detail so it tracks the list's real width and the full row height.
        pos: { $canvas: 'relative' },
        _before: {
          ...insetDivider({ orientation: 'vertical' }),
          left: '-1px'
        },
        // Page-scroll so the footer can stick to the viewport (Events). Legacy
        // keeps the inner pane as the scrollport.
        overflow: { $base: 'auto', $canvas: 'visible' },
        ...sx
      })}
      {...containerSmartAriaLabelProps}
      {...props}
    />
  )
}

/**
 * SplitList.DetailHeader
 *
 * This component is a child component of SplitList.Detail that represents the header of the content for a list item.
 *
 * This component accepts FlexProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 *
 * @agent-gotcha Put the selected item title in SplitList.DetailHeading as the first child (or inside a simple layout wrapper). Do not hand-roll Heading/textStyle.
 */
export const DetailHeader = forwardRef(({ children, sx, ...props }: FlexProps, ref) => (
  <Flex
    id="split-list-header"
    ref={ref}
    justifyContent="space-between"
    bg="white"
    data-items-list-header
    data-split-list-header
    as="header"
    align="center"
    sx={{
      ...$thm({
        minH: { $base: '72px', $canvas: 0 },
        px: { $base: 6, $canvas: 0 },
        py: { $base: 3, $canvas: 0 },
        mb: { $base: 0, $canvas: 6 },
        borderBottom: { $base: '1px solid var(--vndly-color-container-border)', $canvas: 'none' }
      }),
      ...sx
    }}
    {...props}>
    {children}
  </Flex>
))

DetailHeader.displayName = 'SplitList.DetailHeader'

/**
 * SplitList.DetailHeading
 *
 * The heading text for a `SplitList.DetailHeader`. Use this inside of `SplitList.DetailHeader`.
 *
 * @agent-gotcha Put the selected item's title here inside SplitList.DetailHeader. Do not hand-roll a Heading/textStyle.
 */
export const DetailHeading = forwardRef((props: HeadingProps, ref) => (
  <Heading textStyle="heading3" data-split-list-heading ref={ref} {...props} />
))

DetailHeading.displayName = 'SplitList.DetailHeading'

/**
 * SplitList.DetailContent
 *
 * This component is a child component of SplitList.Detail that represents the body of the content for a list item.
 *
 * This component accepts BoxProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const DetailContent = forwardRef(({ children, sx, ...props }: BoxProps, ref) => (
  <Box
    data-split-list-content
    flexGrow={1}
    ref={ref}
    sx={$thm({
      p: { $base: 6, $canvas: 0 },
      ...sx
    })}
    {...props}>
    {children}
  </Box>
))

DetailContent.displayName = 'SplitList.DetailContent'

/**
 * SplitList.DetailFooter
 *
 * This component is a child component of SplitList that represents the footer of the content for a list item.
 *
 * This component accepts FlexProps as input params.
 *
 * See: /docs/design-system/modules/lists/split-list/split-list
 */
export const DetailFooter = forwardRef(({ sx, ...props }: FlexProps, ref) => (
  <Flex
    ref={ref}
    data-split-list-footer
    as="footer"
    position="sticky"
    bottom="0"
    justifyContent="space-between"
    bg="white"
    align="center"
    px={6}
    py={3}
    minH="72px"
    borderTopWidth="1px"
    borderColor="var(--vndly-color-container-border)"
    zIndex="sticky"
    sx={{
      ...$thm({
        mt: { $canvas: 6 },
        borderColor: { $canvas: 'var(--cnvs-sys-color-border-divider)' },
        _before: {
          $canvas: {
            content: '""',
            position: 'absolute',
            left: 0,
            width: 'calc(100% - 1px)',
            height: 6,
            background: 'linear-gradient(360deg, #ffffff, transparent)',
            bottom: 'calc(100% + 1px)'
          }
        }
      }),
      ...sx
    }}
    {...props}
  />
))

DetailFooter.displayName = 'SplitList.DetailFooter'
